import { Descriptions } from "@/components/v2";
import { Tag, type DescriptionsProps } from "antd";
import type { ServicePartner } from "@rumble-it/common-lib/types/models/ServicePartner.interface";
import type { ServicePartnerTagSummary } from "@rumble-it/common-lib/types/models/ServicePartnerTag.interface";
import { getMapsLink } from "@/lib/helpers/links";
import { Card } from "@rumbledirect/ui";

interface ServicePartnerCardProps {
  servicePartners: ServicePartner[] | undefined;
  isLoading: boolean;
}

export const ServicePartnersCard = ({
  servicePartners,
  isLoading,
}: ServicePartnerCardProps) => {
  // Display the first service partner if available
  const servicePartner = servicePartners?.[0];
  const servicePartnerTags =
    servicePartner?.servicePartnerTags ?? servicePartner?.tags ?? [];

  const getServicePartnerTagKey = (tag: ServicePartnerTagSummary) =>
    tag.id ?? tag["@id"] ?? tag.name;

  const items: DescriptionsProps["items"] = [
    {
      label: "Hersteller",
      children: servicePartner?.fullName,
    },
    {
      label: "E-mailadres",
      children: servicePartner?.email ? (
        <a href={`mailto:${servicePartner.email}`}>{servicePartner.email}</a>
      ) : null,
    },
    {
      label: "Telefoonnummer",
      children: servicePartner?.telephone ? (
        <a href={`tel:${servicePartner.telephone}`}>
          {servicePartner.telephone}
        </a>
      ) : null,
    },
    {
      label: "Adres",
      //TODO: Use address component
      children: servicePartner?.address ? (
        <>
          <a
            href={getMapsLink({ address: servicePartner.address })}
            target="_blank"
            rel="noopener noreferrer"
          >
            {`${servicePartner.address.street} ${servicePartner.address.streetNumber}${servicePartner.address.streetNumberAddition || ""}`}{" "}
            <br />
            {servicePartner.address.postalCode} {servicePartner.address.city}{" "}
            {servicePartner.address.country ? (
              <>
                <br />
                {servicePartner.address.country}
              </>
            ) : null}
          </a>
        </>
      ) : null,
    },
    {
      label: "Postadres",
      //TODO: Use address component
      children: servicePartner?.postalAddress ? (
        <>
          {`${servicePartner.postalAddress.street} ${servicePartner.postalAddress.streetNumber}${servicePartner.postalAddress.streetNumberAddition || ""}`}{" "}
          <br />
          {servicePartner.postalAddress.postalCode}{" "}
          {servicePartner.postalAddress.city}{" "}
          {servicePartner.postalAddress.country ? (
            <>
              <br />
              {servicePartner.postalAddress.country}
            </>
          ) : null}
        </>
      ) : null,
    },
  ];

  return (
    <Card
      loading={isLoading}
      className="w-full"
      title="Hersteller"
      extra={
        servicePartnerTags.length > 0 ? (
          <div className="flex flex-wrap justify-end gap-1">
            {servicePartnerTags.map((tag) => {
              const tagKey = getServicePartnerTagKey(tag);

              return tagKey ? (
                <Tag key={tagKey} className="m-0">
                  {tag.name ?? tag.id ?? tag["@id"]}
                </Tag>
              ) : null;
            })}
          </div>
        ) : null
      }
    >
      {!servicePartner ? (
        <p>Geen hersteller gekoppeld aan dit dossier.</p>
      ) : (
        <Descriptions items={items} />
      )}
      {servicePartners && servicePartners.length > 1 ? (
        <p style={{ marginTop: 16, fontStyle: "italic" }}>
          En {servicePartners.length - 1} andere hersteller(s)
        </p>
      ) : null}
    </Card>
  );
};
